import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { 
  LayoutDashboard, 
  Users, 
  GraduationCap, 
  UserCheck, 
  BookOpen, 
  UsersRound, 
  ClipboardCheck, 
  FileText,
  Calendar,
  Settings,
  Menu,
  X,
  LogOut,
  ShieldAlert
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

const Layout = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { hasPermission, isAdmin } = useAuth();

  // Menu items with permission requirements
  const allMenuItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', permission: null }, // Dashboard sempre visível
    { path: '/children', icon: Users, label: 'Crianças', permission: 'children_view' },
    { path: '/professionals', icon: UserCheck, label: 'Profissionais', permission: 'professionals_view' },
    { path: '/activities', icon: BookOpen, label: 'Atividades', permission: 'activities_view' },
    { path: '/groups', icon: UsersRound, label: 'Grupos', permission: 'groups_view' },
    { path: '/enrollments', icon: GraduationCap, label: 'Matrículas', permission: 'enrollments_view' },
    { path: '/attendance', icon: ClipboardCheck, label: 'Chamada', permission: 'attendance_view' },
    { path: '/reports', icon: FileText, label: 'Relatórios', permission: 'reports_view' },
    { path: '/events', icon: Calendar, label: 'Agenda', permission: 'events_view' },
    { path: '/users', icon: Settings, label: 'Usuários', permission: 'users_manage' },
  ];

  // Filter menu items based on permissions
  const menuItems = useMemo(() => {
    return allMenuItems.filter(item => {
      // No permission required, always show
      if (!item.permission) return true;
      // Check if user has permission
      return hasPermission(item.permission);
    });
  }, [hasPermission]);

  // Check if current route is allowed
  const currentRouteAllowed = useMemo(() => {
    const currentItem = allMenuItems.find(item => item.path === location.pathname);
    if (!currentItem) return true; // Unknown route, let it through
    if (!currentItem.permission) return true; // No permission required
    return hasPermission(currentItem.permission);
  }, [location.pathname, hasPermission]);

  const handleNavigate = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  // Redirect if trying to access a page without permission
  if (!currentRouteAllowed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800 mb-2">Acesso Negado</h2>
          <p className="text-slate-600 mb-6">
            Você não tem permissão para acessar esta página.
          </p>
          <Button onClick={() => navigate('/')} className="bg-[#D87A4E] hover:bg-[#c26a42]">
            Voltar ao Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-orange-200 flex items-center justify-between px-4 z-40">
        {/* Logo apenas - sem texto */}
        <img src="/logo.png" alt="Logo" className="h-12 w-auto object-contain" />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white border-r border-orange-200 z-50 transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-28 flex items-center justify-center border-b border-orange-200 bg-gradient-to-r from-[#D87A4E]/5 to-[#7A3D36]/5 px-4 py-6">
            <img src="/logo.png" alt="Divino Oleiro Mirim" className="h-20 w-auto object-contain" data-testid="sidebar-logo" />
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-orange-200">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isAdmin() ? 'bg-amber-200' : 'bg-[#D87A4E]/20'}`}>
                <span className={`font-semibold ${isAdmin() ? 'text-amber-700' : 'text-[#D87A4E]'}`}>
                  {user.full_name?.charAt(0) || user.username?.charAt(0)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 truncate">{user.full_name || user.username}</p>
                <p className="text-xs text-slate-500 truncate">
                  {isAdmin() ? (
                    <span className="text-amber-600 font-medium">Administrador</span>
                  ) : user.role}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigate(item.path)}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    isActive
                      ? 'bg-[#D87A4E]/15 text-[#D87A4E] font-medium'
                      : 'text-slate-600 hover:bg-orange-50'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-orange-200">
            <Button
              variant="ghost"
              className="w-full justify-start text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={onLogout}
              data-testid="logout-button"
            >
              <LogOut className="h-5 w-5 mr-3" />
              Sair
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0">
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;